import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Shield, Users, Calculator, Home, Star, CheckCircle } from "lucide-react";

const HomePage = () => {
  const benefits = [
    {
      icon: Shield,
      title: "Veteran Owned & Operated",
      description: "Founded and operated by veterans who understand the value of service, integrity, and dedication to our clients."
    },
    {
      icon: Users,
      title: "Multiple Lender Network",
      description: "Access to dozens of lenders means we find the best rates and terms for your unique situation."
    },
    {
      icon: Calculator,
      title: "Expert Guidance",
      description: "Our experienced loan advisors guide you through every step of the mortgage process."
    },
    {
      icon: Home,
      title: "Local Market Expertise",
      description: "Deep knowledge of Colorado real estate markets and local lending requirements."
    }
  ];

  const brokerAdvantages = [
    "Access to multiple lenders for competitive rates",
    "Personalized service throughout the process",
    "Expert knowledge of various loan programs",
    "Local market expertise and relationships"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 to-blue-50 py-20 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Your Pathway Home
              <span className="text-[#0b2c4a] block"> begins here</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Experience the difference of working with a veteran-owned mortgage brokerage. 
              We find the best loan programs and rates from our extensive lender network.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white px-8 py-3"
                asChild
              >
                <Link to="/calculators">Calculate Your Payment</Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-[#0b2c4a] text-[#0b2c4a] hover:bg-[#0b2c4a] hover:text-white px-8 py-3"
                asChild
              >
                <Link to="/team">Meet Our Team</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose a Broker Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Work with a Mortgage Broker?
            </h2>
            <p className="text-lg text-gray-600">
              Unlike banks that offer only their own products, we shop multiple lenders 
              to find the best rates and terms for your unique situation.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              {brokerAdvantages.map((advantage, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-[#0b2c4a] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{advantage}</span>
                </div>
              ))}
            </div>
            <Card className="p-8 bg-slate-50 border-none">
              <CardContent className="p-0">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0b2c4a] rounded-full mb-4">
                    <Star className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Your Success is Our Mission
                  </h3>
                  <p className="text-gray-600">
                    With our veteran values of integrity, service, and commitment, 
                    we're dedicated to helping you achieve your homeownership goals.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              The Pathway Home Lending Advantage
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the difference of our veteran-owned approach to mortgage lending.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center p-6 hover:shadow-lg transition-shadow border-none bg-white">
                <CardContent className="p-0">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-[#0b2c4a]/10 rounded-lg mb-4">
                    <benefit.icon className="h-6 w-6 text-[#0b2c4a]" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#545454]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Get pre-qualified in minutes or speak with one of our experienced loan advisors today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-[#545454] hover:bg-gray-100 px-8 py-3"
              asChild
            >
              <Link to="/calculators">Use Our Calculators</Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-[#545454] px-8 py-3"
              asChild
            >
              <Link to="/loan-programs">View Loan Programs</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;